<template>
  <div id="app">
    <b-loading :active="isLoading"></b-loading>
    <b-navbar :shadow="true" type="is-light" :fixed-top="true">
      <template slot="brand">
        <b-navbar-item tag="router-link" :to="{ path: '/projects' }">
          <img
              src="./assets/omexom.png"
              :alt="$t('top-menu.logo')"
          >
        </b-navbar-item>
      </template>
      <template slot="start">
        <b-navbar-item v-if="isLoggedIn && currentUserAccess('Projects', this.UserActions.VIEW)" href="#/projects">
          <b-icon icon="android-studio" class="menu-icon"/>
          {{ $t('top-menu.projects') }}
        </b-navbar-item>
        <b-navbar-item v-if="isLoggedIn && currentUserAccess('ProjectTemplates', this.UserActions.VIEW)"
                       href="#/templates">
          <b-icon icon="content-duplicate" class="menu-icon"/>
          {{ $t('top-menu.project-templates') }}
        </b-navbar-item>
        <b-navbar-item v-if="isLoggedIn && this.currentUserAccess('Users', this.UserActions.VIEW)" href="#/users">
          <b-icon icon="account-group" class="menu-icon"/>
          {{ $t('top-menu.users') }}
        </b-navbar-item>
        <b-navbar-item v-if="isLoggedIn && this.currentUserAccess('CategoricalData', this.UserActions.VIEW)"
                       href="#/categories">
          <b-icon icon="shape" class="menu-icon"/>
          {{ $t('top-menu.categories') }}
        </b-navbar-item>
      </template>

      <template slot="end">
        <b-navbar-item tag="div">
          <b-navbar-dropdown :label="username" v-if="isLoggedIn" :hoverable="true">
            <b-navbar-item @click="showUserAccountClicked">
              <b-icon icon="account-cog" class="menu-icon"/>
              {{ $t('top-menu.account') }}
            </b-navbar-item>
            <b-navbar-item @click="logout">
              <b-icon icon="logout" class="menu-icon"/>
              {{ $t('top-menu.sign-out') }}
            </b-navbar-item>
          </b-navbar-dropdown>
          <div class="columns is-vcentered is-mobile">
            <div class="column is-narrow" v-if="!isLoggedIn">
              <button class="button is-primary" @click="showSignUp">
                <b-icon icon="account-plus" class="menu-icon"/>
                {{ $t('top-menu.sign-up') }}
              </button>
            </div>
            <div class="column is-narrow" v-if="!isLoggedIn">
              <button class="button is-primary is-outlined" @click="showLogin">
                <b-icon icon="login" class="menu-icon"/>
                {{ $t('top-menu.login') }}
              </button>
            </div>
            <div v-if="currentUserSystemAccessLevel()" class="column is-narrow">
              <b-dropdown v-model="$i18n.locale" :triggers="['hover']" aria-role="list" style="margin-left: 8px">
                <button class="button is-primary" slot="trigger">
                  <span>{{ $i18n.locale }}</span>
                  <b-icon icon="menu-down"/>
                </button>
                <b-dropdown-item v-for="(lang, i) in languages" :key="`Lang${i}`" :value="lang" aria-role="listitem">{{
                    lang
                  }}
                </b-dropdown-item>
              </b-dropdown>
            </div>
          </div>
        </b-navbar-item>
      </template>
    </b-navbar>

    <router-view :key="$route.path"/>

    <footer class="app-footer">
      <div class="has-text-centered">
        <p>Powered by <a href="https://bald.studio" target="_blank">Bald.studio</a> &copy; {{ getCurrentYear() }} </p>
      </div>
      <div v-if="currentUserSystemAccessLevel()" class="has-text-centered">
        <br>
        <b-select v-model="selectedEndPoint" @input="endpointSelected()">
          <option
              v-for="endpoint in endpoints"
              :value="endpoint"
              :key="endpoint">
            {{ endpoint }}
          </option>
        </b-select>
      </div>
    </footer>
  </div>
</template>

<script>
import firebase from "firebase/app";

export default {
  name: 'App',
  data() {
    return {
      isLoading: false,
      languages: ['fr', 'en'],
      userRole: {},
      isLoggedIn: false,
      username: null,
      endpoints: ['submit', 'submitDev'],
      selectedEndPoint: this.getSubmitEndpoint()
    };
  },
  methods: {
    showLogin: function () {
      this.$router.replace("login");
    },
    showSignUp: function () {
      this.$router.replace("sign-up");
    },
    logout: function () {
      firebase.auth().signOut()
          .then(() => {
            localStorage.removeItem("currentUser")
            this.removeUserRole()
            this.$router.replace("/").catch(() => {
            });
          });
    },
    showUserAccountClicked: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const getUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getUserFunction({action: "get_users", filters: [['email', '==', this.username]], fill_meta: true})
          .then((result) => {
            this.isLoading = false
            console.log("result: ", result.data);
            const usersData = result.data["users"];
            if (usersData.length > 0) {
              this.showUserDetails(usersData[0].document_id)
            } else {
              // create user and then show details
              this.createUser(this.username)
            }
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(error.message)
            console.log("Error getting user: ", error);
          });
    },
    createUser: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const insertUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      insertUserFunction({
        action: "insert_user",
        email: this.username
      }).then((result) => {
        this.isLoading = false;
        console.log("create user result: ", result.data);
        const insertedUsersData = result.data.payload["users"].inserted
        if (insertedUsersData.length > 0) {
          this.showUserDetails(insertedUsersData[0].document_id)
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError("Error writing document: " + error);
      });
    },
    showUserDetails: function (document_id) {
      const path = `/users/${document_id}`
      if (this.$route.path !== path) {
        this.$router.push({
          name: 'UserDetails',
          params: {
            id: document_id
          }
        })
      }
    },
    getCurrentYear: function () {
      return new Date().getFullYear()
    },
    endpointSelected() {
      this.saveSubmitEndpoint(this.selectedEndPoint)
    }
  },
  mounted() {
    // load user role again from the server on each app refresh!
    this.loadUserRoleFromServer().then((userRole) => {
      this.userRole = userRole
    })
  },
  created() {
    firebase.auth().onAuthStateChanged(() => {
      const currentUser = firebase.auth().currentUser
      if (currentUser) {
        console.log("onAuthStateChanged logged in")
        localStorage.setItem("currentUser", JSON.stringify(currentUser))
      } else {
        console.log("onAuthStateChanged logged out")
        localStorage.removeItem("currentUser")
      }
      this.isLoggedIn = firebase.auth().currentUser != null && firebase.auth().currentUser.emailVerified
      if (this.isLoggedIn) {
        this.username = firebase.auth().currentUser.email
      } else {
        this.username = null
      }
    });
  }
}
</script>
<style>
.menu-icon {
  margin-right: 6px !important;
}

.internal-text {
  color: #0071ce;
  font-weight: bold;
}

.external-text {
  color: #FFAF37;
  font-weight: bold;
}

.app-footer {
  background-color: #FAFAFA;
  padding: 3rem 1.5rem 3rem;
}
</style>